/**
 * Parent Origin Utilities
 * Centralized origin whitelist for iframe communication security.
 * Used by IframeGuard, AuthContext, PersonalityContext, and all postMessage senders.
 */

/**
 * Validate a user-supplied origin string before adding it to the whitelist.
 * Rules:
 *   - Must be a valid URL
 *   - Must be https:// (localhost https/http allowed in dev only)
 *   - Must not contain a path — origin only: scheme://host[:port]
 *   - Hostname must have a known TLD OR be localhost/127.0.0.1/::1
 */
function isValidExtraOrigin(candidate: string): boolean {
  try {
    const u = new URL(candidate);
    if (u.pathname !== '/' && u.pathname !== '') return false;
    if (u.search || u.hash) return false;
    const host = u.hostname;
    const isLocal =
      host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost');
    if (u.protocol === 'https:') {
      if (isLocal) return true;
      // Require a TLD of 2+ letters — rejects raw hostnames like "intranet".
      return /\.[a-z]{2,}$/i.test(host);
    }
    if (u.protocol === 'http:' && isLocal) return true;
    return false;
  } catch {
    return false;
  }
}

const isProdBuild = import.meta.env?.PROD === true;

const EXTRA: string[] = isProdBuild
  ? []                           // Production builds never accept runtime-supplied extra origins.
  : (import.meta.env?.VITE_EXTRA_PARENT_ORIGINS || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
      .filter(isValidExtraOrigin);

export const ALLOWED_PARENT_ORIGINS: string[] = [
  // Production
  'https://education.bankvaultacademy.org',
  // Development
  'https://education.dev.bankvaultacademy.org',
  // Local development (ports 5173-5180)
  'http://localhost:5173',
  'http://localhost:5174',
  'http://localhost:5175',
  'http://localhost:5176',
  'http://localhost:5177',
  'http://localhost:5178',
  'http://localhost:5179',
  'http://localhost:5180',
  'http://127.0.0.1:5173',
  'http://127.0.0.1:5174',
  'http://127.0.0.1:5175',
  'http://127.0.0.1:5176',
  'http://127.0.0.1:5177',
  'http://127.0.0.1:5178',
  'http://127.0.0.1:5179',
  'http://127.0.0.1:5180',
  ...EXTRA,
];

/**
 * Check if an origin is in the allowed whitelist (exact match).
 */
export function isAllowedOrigin(origin: string | null | undefined): boolean {
  return !!origin && ALLOWED_PARENT_ORIGINS.includes(origin);
}

/**
 * Cached verified parent origin, set when handshake succeeds.
 * Used as the target origin for outbound postMessage calls.
 */
let verifiedParentOrigin: string | null = null;

/**
 * Set the verified parent origin after a successful handshake.
 */
export function setVerifiedParentOrigin(origin: string): void {
  verifiedParentOrigin = origin;
}

/**
 * Get the verified parent origin for outbound postMessage calls.
 * Returns null if no handshake has occurred yet.
 */
export function getVerifiedParentOrigin(): string | null {
  return verifiedParentOrigin;
}

/**
 * Reset the verified parent origin. Called on logout / clearAuth so that
 * any further postMessage attempts require a fresh handshake.
 */
export function resetVerifiedParentOrigin(): void {
  verifiedParentOrigin = null;
}

/**
 * Send a postMessage to the parent window using the verified origin.
 * Refuses to send if no verified origin is set (no fallback).
 * Does nothing if not in an iframe.
 */
export function sendToParent(message: unknown): void {
  if (window.parent === window) return;

  if (!verifiedParentOrigin) {
    return; // No verified parent yet — silently drop. Handshake will retry.
  }
  const targetOrigin = verifiedParentOrigin;
  window.parent.postMessage(message, targetOrigin);
}
